// src/pages/Honorarios/HonorariosAdminHome.js
//
// Pantalla principal de Administración de Honorarios — Oficina de Pagos.
// Reorganización Sesión 3 del rediseño: header + pipeline + tabs reordenados
// según el flujo real de trabajo de la Oficina.
//
// Flujo conceptual de la Oficina:
//   1) El abogado crea solicitudes (en su panel Honorarios)
//   2) Oficina las revisa y las pone en proceso de pago
//   3) Oficina genera lotes de pago (agrupa solicitudes)
//   4) Lotes pagados quedan como histórico
//
// La pestaña Auditoría se movió al módulo independiente "/auditoria".

import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Container, Paper, Typography, Stack, Tabs, Tab, Tooltip, IconButton,
    CircularProgress, Alert, Divider,
} from "@mui/material";
import {
    Payments as PaymentsIcon,
    AssignmentTurnedIn as SolicitudesIcon,
    Inventory2 as LotesIcon,
    Description as CertificadosIcon,
    Insights as DashboardIcon,
    Refresh as RefreshIcon,
    ChevronRight as ChevronRightIcon,
    HourglassEmpty as HourglassIcon,
    CheckCircle as CheckCircleIcon,
    AccountBalanceWallet as WalletIcon,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { SnackbarProvider } from 'notistack';

import { usePermisos } from "../../utils/usePermisos";
import { honorariosAdminDashboardJWT } from "../../api/endpoints";
import SolicitudesAdminTable from "./SolicitudesAdminTable";
import HonorariosAdminDashboard from "./HonorariosAdminDashboard";
import CertificadosAdminTable from "./CertificadosAdminTable";
import LotesAdminTable from "./LotesAdminTable";

const COLOR = '#2e7d32'; // verde Honorarios (paleta del sistema)
const BR = 4;

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}
            id={`hadmin-tabpanel-${index}`}
            aria-labelledby={`hadmin-tab-${index}`}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

function fmt(n) { return Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 }); }
function fmtMonto(n) { return Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function HonorariosAdminHome() {
    const { tienePermiso } = usePermisos();
    const navigate = useNavigate();

    const puedeAdmin = tienePermiso("PLANES DE PAGO - HONORARIOS COMPARTIDOS Y EN CUOTAS (ADMIN)");
    const puedeAuditar = tienePermiso(
        "PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS",
        "SELECT",
        "EJECUCIONES TRIBUTARIAS Y OTROS REQUERIMIENTOS DE CRÉDITOS FISCALES",
    );

    const [tab, setTab] = useState(0);
    const [totales, setTotales] = useState({
        total_historico: 0, total_solicitudes: 0, promedio_solicitud: 0,
        total_pendiente: 0, total_en_proceso: 0,
        cant_pendiente: 0, cant_en_proceso: 0,
    });
    const [cargando, setCargando] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const cargarTotales = useCallback(async () => {
        setCargando(true);
        try {
            const year = new Date().getFullYear();
            const r = await honorariosAdminDashboardJWT(year);
            if (r?.ok && r.totales) setTotales(r.totales);
        } catch { /* silencioso */ }
        finally { setCargando(false); }
    }, []);

    useEffect(() => { if (puedeAdmin) cargarTotales(); }, [puedeAdmin, cargarTotales, reloadKey]);

    const refrescar = () => setReloadKey(k => k + 1);

    if (!puedeAdmin) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    No tenés permiso para Administración de Honorarios. Pedile a un administrador el permiso
                    <strong> "PLANES DE PAGO - HONORARIOS COMPARTIDOS Y EN CUOTAS (ADMIN)"</strong>.
                </Alert>
            </Box>
        );
    }

    // Atención: cosas que requieren acción de la oficina
    const items = [];
    if (totales.cant_en_proceso > 0) {
        items.push({
            key: 'enproceso', icon: <HourglassIcon sx={{ color: '#f57c00' }} />, color: '#f57c00',
            label: `${fmt(totales.cant_en_proceso)} solicitudes en proceso de pago`,
            sub: `Total: $${fmtMonto(totales.total_en_proceso)} — esperan resolución de la Oficina`,
            onClick: () => setTab(1),
        });
    }
    if (totales.cant_pendiente > 0) {
        items.push({
            key: 'pend', icon: <SolicitudesIcon sx={{ color: '#c62828' }} />, color: '#c62828',
            label: `${fmt(totales.cant_pendiente)} solicitudes nuevas para revisar`,
            sub: `Total: $${fmtMonto(totales.total_pendiente)} — recién creadas por abogados`,
            onClick: () => setTab(1),
        });
    }

    return (
        <SnackbarProvider maxSnack={3}>
            <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
                <Container maxWidth={false} disableGutters>
                    {/* ── Header ────────────────────────────────────────────── */}
                    <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                        <PaymentsIcon sx={{ color: COLOR, fontSize: 36 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                                Honorarios — Oficina de Pagos
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Gestión de solicitudes de honorarios y lotes de pago
                            </Typography>
                        </Box>
                        <Tooltip title="Refrescar">
                            <IconButton onClick={refrescar} disabled={cargando}>
                                {cargando ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                        {puedeAuditar && (
                            <Tooltip title="La pestaña Auditoría se movió a su módulo independiente">
                                <span>
                                    <IconButton onClick={() => navigate('/auditoria')}
                                        sx={{ color: COLOR }}>
                                        <OpenInNewIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* ── Pipeline ──────────────────────────────────────────── */}
                    <Box sx={{ display: 'grid', gap: 1.5,
                        gridTemplateColumns: { xs: '1fr', md: '1fr 32px 1fr 32px 1fr' },
                        alignItems: 'stretch', mb: 2 }}>
                        <PipelineCard
                            num="1" icon={<SolicitudesIcon />} titulo="SOLICITUDES NUEVAS"
                            color={totales.cant_pendiente > 0 ? '#c62828' : COLOR}
                            valorPrincipal={fmt(totales.cant_pendiente)}
                            valorLabel="esperan revisión"
                            lineas={[
                                { label: 'Total $', valor: `$${fmtMonto(totales.total_pendiente)}` },
                                { label: 'Acción', valor: 'revisar y pasar a proceso' },
                            ]}
                            onClick={() => setTab(1)}
                        />
                        <FlechaPipeline />
                        <PipelineCard
                            num="2" icon={<HourglassIcon />} titulo="EN PROCESO"
                            color={totales.cant_en_proceso > 0 ? '#f57c00' : COLOR}
                            valorPrincipal={fmt(totales.cant_en_proceso)}
                            valorLabel="en lote / pago"
                            lineas={[
                                { label: 'Total $', valor: `$${fmtMonto(totales.total_en_proceso)}` },
                                { label: 'Acción', valor: 'generar lote / pagar' },
                            ]}
                            onClick={() => setTab(2)}
                        />
                        <FlechaPipeline />
                        <PipelineCard
                            num="3" icon={<CheckCircleIcon />} titulo="HISTÓRICO PAGADO" color={COLOR}
                            valorPrincipal={fmt(totales.total_solicitudes)}
                            valorLabel="solicitudes acumuladas"
                            lineas={[
                                { label: 'Total movido', valor: `$${fmtMonto(totales.total_historico)}` },
                                { label: 'Promedio', valor: `$${fmtMonto(totales.promedio_solicitud)}` },
                            ]}
                            onClick={() => setTab(0)}
                        />
                    </Box>

                    {/* ── Atención ─────────────────────────────────────────── */}
                    {items.length > 0 && (
                        <Paper sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px`, borderLeft: '4px solid #f57c00' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#666' }}>
                                ⚠ Requieren acción de la oficina ({items.length})
                            </Typography>
                            <Stack divider={<Divider />} spacing={0}>
                                {items.map(it => (
                                    <AtencionItem key={it.key} icon={it.icon} color={it.color}
                                        label={it.label} sub={it.sub} onClick={it.onClick} />
                                ))}
                            </Stack>
                        </Paper>
                    )}

                    {/* ── Tabs (sin Auditoría — está en su módulo) ──────────── */}
                    <Paper sx={{ borderRadius: `${BR}px`, overflow: 'hidden' }}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{
                                borderBottom: '1px solid #eee',
                                bgcolor: '#fafbfc',
                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                                '& .Mui-selected': { color: COLOR + ' !important' },
                                '& .MuiTabs-indicator': { backgroundColor: COLOR },
                            }}
                        >
                            <Tab icon={<DashboardIcon />} iconPosition="start" label="Dashboard" />
                            <Tab icon={<SolicitudesIcon />} iconPosition="start" label="Solicitudes de Pago" />
                            <Tab icon={<LotesIcon />} iconPosition="start" label="Lotes de Pago" />
                            <Tab icon={<CertificadosIcon />} iconPosition="start" label="Certificados" />
                            {puedeAuditar && (
                                <Tab
                                    icon={<WalletIcon />} iconPosition="start"
                                    label="Auditoría (movido)"
                                    sx={{ opacity: 0.6 }}
                                />
                            )}
                        </Tabs>

                        <Box sx={{ p: 2 }}>
                            <TabPanel value={tab} index={0}>
                                <HonorariosAdminDashboard />
                            </TabPanel>
                            <TabPanel value={tab} index={1}>
                                <SolicitudesAdminTable />
                            </TabPanel>
                            <TabPanel value={tab} index={2}>
                                <LotesAdminTable />
                            </TabPanel>
                            <TabPanel value={tab} index={3}>
                                <CertificadosAdminTable />
                            </TabPanel>
                            {puedeAuditar && (
                                <TabPanel value={tab} index={4}>
                                    <Alert
                                        severity="info"
                                        action={
                                            <button
                                                onClick={() => navigate('/auditoria')}
                                                style={{ background: COLOR, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                                            >
                                                Ir a Auditoría →
                                            </button>
                                        }
                                    >
                                        Esta pestaña se movió al módulo independiente <strong>Auditoría</strong>
                                        {' '}(menú izquierdo). La versión nueva incluye pipeline de supervisión y atención por excepción.
                                    </Alert>
                                </TabPanel>
                            )}
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </SnackbarProvider>
    );
}

// ============================================================================
// Componentes visuales (mismo patrón de los otros centros)
// ============================================================================
function PipelineCard({ num, icon, titulo, color, valorPrincipal, valorLabel, lineas = [], onClick }) {
    return (
        <Paper variant="outlined" onClick={onClick}
            sx={{
                p: 2, borderRadius: `${BR}px`, cursor: onClick ? 'pointer' : 'default',
                borderTop: `3px solid ${color}`, transition: 'all 0.15s',
                '&:hover': onClick ? { boxShadow: 2, transform: 'translateY(-2px)' } : {},
                display: 'flex', flexDirection: 'column', gap: 1,
            }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ bgcolor: color, color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                    {num}
                </Box>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', letterSpacing: 0.5 }}>
                    {titulo}
                </Typography>
            </Stack>
            <Box sx={{ pl: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                    {valorPrincipal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {valorLabel}
                </Typography>
            </Box>
            {lineas.length > 0 && (
                <Stack spacing={0.25} sx={{ pt: 0.5, borderTop: '1px dashed #eee' }}>
                    {lineas.map((l, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{l.valor}</Typography>
                        </Box>
                    ))}
                </Stack>
            )}
        </Paper>
    );
}

function FlechaPipeline() {
    return (
        <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center',
            color: '#bbb',
        }}>
            <ChevronRightIcon sx={{ fontSize: 32 }} />
        </Box>
    );
}

function AtencionItem({ icon, color, label, sub, onClick }) {
    return (
        <Box onClick={onClick} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        }}>
            <Box sx={{ display: 'flex' }}>{icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color }}>{label}</Typography>
                <Typography variant="caption" color="text.secondary">{sub}</Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#999' }} />
        </Box>
    );
}
